'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { StoryScrollytellingData } from './schema';

export function StoryScrollytellingRenderer({
  eyebrow,
  title,
  titleHl,
  subtitle,
  linkLabel,
  linkHref,
  videoPoster,
  videoPosterFallback,
  videoAlt,
  videoCaption,
  chapters,
}: StoryScrollytellingData) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const safeChapters = Array.isArray(chapters) ? chapters : [];

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    function onScroll() {
      const el = scrollerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      if (total <= 0) return;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const pct = scrolled / total;
      setProgress(pct);
      const idx = Math.min(
        safeChapters.length - 1,
        Math.floor(pct * safeChapters.length),
      );
      setActiveIdx(idx);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [safeChapters.length]);

  const active = safeChapters[activeIdx] ?? safeChapters[0];
  if (!active) return null;
  const current = String(activeIdx + 1).padStart(2, '0');
  const total = String(safeChapters.length).padStart(2, '0');

  return (
    <section className="v5-light v5-section v5-story-section" id="video">
      <div className="container">
        <div className="v5-story-head">
          <div className="v5-story-head-left">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            <h2 className="v5-story-h2">
              {title}
              {titleHl ? <> <span className="v5-story-h2-hl">{titleHl}</span></> : null}
            </h2>
          </div>
          <div className="v5-story-head-right">
            {subtitle ? <p className="v5-story-sub">{subtitle}</p> : null}
            {linkLabel && linkHref ? (
              <a href={linkHref} className="v5-story-link">
                {linkLabel}
                <span className="v5-story-link-arrow">→</span>
              </a>
            ) : null}
          </div>
        </div>
        <div
          className="v5-story-scroller"
          data-stats-count={safeChapters.length}
          ref={scrollerRef}
          style={{ ['--story-scroll-h']: `${safeChapters.length * 100}vh` } as CSSProperties}
        >
          <div className="v5-story-stage">
            <div className="v5-story-stage-video">
              <div className="v5-story-video">
                <div className="v5-story-video-glow"></div>
                <div className="v4-video-frame v5-story-video-frame">
                  <img
                    className="v4-video-poster"
                    src={videoPoster}
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (videoPosterFallback && img.src !== videoPosterFallback) {
                        img.src = videoPosterFallback;
                      }
                    }}
                    alt={videoAlt ?? ''}
                  />
                  <button className="v4-play-btn" aria-label="Reproducir vídeo">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                  </button>
                  <div className="v5-story-video-badge">
                    <span className="dot"></span>
                    <span>{active.badge}</span>
                  </div>
                  <div className="v4-video-caption v5-story-video-caption">
                    <strong>{videoCaption.title}</strong>
                    <span>{videoCaption.sub}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="v5-story-stage-stats">
              <div className="v5-story-progress">
                <div className="v5-story-progress-meta">
                  <span className="v5-story-progress-label">Scroll para descubrir</span>
                  <span className="v5-story-progress-num">
                    <strong>{current}</strong>
                    <em>/ {total}</em>
                  </span>
                </div>
                <div className="v5-story-progress-bar">
                  <div
                    className="v5-story-progress-fill"
                    style={{ width: `${progress * 100}%` }}
                  ></div>
                </div>
                <div className="v5-story-progress-dots">
                  {safeChapters.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`v5-story-dot${i === activeIdx ? ' is-active' : ''}`}
                      aria-label={`Ir al capítulo ${i + 1}`}
                      onClick={() => {
                        const el = scrollerRef.current;
                        if (!el) return;
                        const rect = el.getBoundingClientRect();
                        const totalH = rect.height - window.innerHeight;
                        const target =
                          window.scrollY + rect.top + (i / safeChapters.length) * totalH;
                        window.scrollTo({ top: target, behavior: 'smooth' });
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className="v5-story-showcase">
                {safeChapters.map((ch, i) => (
                  <article
                    key={i}
                    className={`v5-story-card${i === activeIdx ? ' is-active' : ''}`}
                    data-story-card={i}
                  >
                    <div className="v5-story-card-eyebrow">{ch.eyebrow}</div>
                    <div className="v5-story-card-num">
                      {ch.bigValue}{' '}
                      {ch.bigUnit ? <span className="unit">{ch.bigUnit}</span> : null}
                    </div>
                    <h3 className="v5-story-card-title">{ch.title}</h3>
                    <p className="v5-story-card-desc">{ch.description}</p>
                    {ch.meta && ch.meta.length > 0 ? (
                      <div className="v5-story-card-meta">
                        {ch.meta.map((m, k) => (
                          <span key={k}>● {m}</span>
                        ))}
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
